
import { Plus, Minus, Trash2 } from 'lucide-react';
import { getUnitPriceForQuantity, type CatalogProduct } from '../api/delivery';
import { useCartStore } from '../store/cart';
import { fmt, type Branding } from '../lib/branding';

// Extraído de StorePage.tsx (12/07/2026) pra poder ser reaproveitado no rail
// de upsell da página de produto (ProductPage.tsx), sem duplicar o card do
// zero. Comportamento visual idêntico ao anterior; único acréscimo é o
// `onOpen`, disparado ao clicar na imagem/nome (não nos botões de
// adicionar/quantidade, que continuam existindo e chamando o carrinho
// direto -- clicar em "Adicionar" no rail de upsell não deve abrir a
// página do produto).
//
// 14/08/2026 (revisão de design, comparação com Zé Delivery / iFood):
//   1. RODAPÉ ANCORADO. O `mt-auto` estava só no bloco de preço, mas a linha
//      do desconto progressivo e o botão vinham DEPOIS dele. Resultado: numa
//      linha da grade com um produto com bulkPricing e outro sem (ex. Corona
//      x Água Lebrinha), o "Adicionar" saía em alturas diferentes. Agora
//      preço + selo + CTA vivem num único bloco `mt-auto`, então o CTA fica
//      sempre na mesma linha de base entre cards vizinhos.
//   2. ALVO DE TOQUE 44px. `Adicionar` tinha 36px e os botões +/- do stepper
//      ~24px -- abaixo do mínimo mobile-first do projeto, justamente no
//      controle que o cliente usa pra subir quantidade (e onde mora a
//      margem do desconto progressivo).
//   3. CONTRASTE. Descrição em text-gray-400 (2,85:1) e o selo 🔥 em 10px
//      amber-600 (3,4:1) reprovavam no WCAG AA. A descrição saiu do card da
//      grade (o Zé também não põe -- ela continua inteira na ProductPage) e
//      o selo subiu pra 12px / amber-700.
//   4. FOTO MAIOR. h-36 fixo numa coluna de ~210px deixava o produto pequeno
//      demais pra reconhecer de relance; virou aspect-square, como no Zé.
//   5. `loading="lazy"` -- a home tem ~170 cards numa página só; sem isso o
//      navegador dispara todas as imagens de uma vez no 4G.
export function ProductCard({
  product,
  brand,
  onOpen,
}: {
  product: CatalogProduct;
  brand: Branding;
  onOpen?: () => void;
}) {
  const { addItem, items, updateQuantity, removeItem } = useCartStore();
  const cartItem = items.find((i) => i.product.id === product.id);

  // Melhor faixa do desconto progressivo (a de maior quantidade = menor
  // preço unitário). Antes a linha listava TODAS as faixas
  // ("2un R$4,75/un · 3un R$4,50/un · 4+un R$4,25/un"): três números
  // disputando atenção com o preço principal, em 10px. A tabela completa
  // continua na página do produto, que é onde o cliente decide a quantidade.
  const melhorFaixa = product.bulkPricing && product.bulkPricing.length > 0
    ? product.bulkPricing[product.bulkPricing.length - 1]
    : null;

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden flex flex-col">
      <div
        className={`w-full aspect-square bg-white flex items-center justify-center overflow-hidden ${onOpen ? 'cursor-pointer' : ''}`}
        onClick={onOpen}
        role={onOpen ? 'button' : undefined}
        aria-label={onOpen ? `Ver detalhes de ${product.name}` : undefined}
      >
        {product.imageUrl ? (
          <img
            src={product.imageUrl}
            alt={product.name}
            loading="lazy"
            decoding="async"
            className="w-full h-full object-contain p-2"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <svg className="w-12 h-12 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </div>
        )}
      </div>
      <div className="p-3 flex flex-col gap-1.5 flex-1">
        <p
          className={`text-sm font-semibold text-gray-800 leading-tight line-clamp-2 ${onOpen ? 'cursor-pointer' : ''}`}
          onClick={onOpen}
        >
          {product.name}
        </p>
        {/* Selo "Economize ..." -- é sinal de promoção, não descrição; fica.
            O texto corrido de descrição saiu da grade (ver nota 3 no topo). */}
        {!product.deliveryDescription && product.description?.toLowerCase().startsWith('economize') && (
          <span className="inline-flex items-center gap-1 bg-green-50 text-green-700 text-xs font-semibold px-1.5 py-0.5 rounded-md w-fit">
            🏷️ {product.description.split('·')[0].trim()}
          </span>
        )}

        {/* ── Rodapé ancorado: preço + selo de desconto + CTA ──
            Bloco único com mt-auto pra o "Adicionar" cair sempre na mesma
            altura entre os dois cards da linha. */}
        <div className="mt-auto pt-1 flex flex-col gap-2">
          {product.hasCustomDeliveryPrice && product.deliveryPriceCents < product.salePriceCents ? (
            <div className="flex items-baseline gap-1.5 flex-wrap">
              <span className="text-xs text-gray-500 line-through">{fmt(product.salePriceCents)}</span>
              <span style={{ color: brand.accentColor }} className="text-lg font-bold tracking-tight">{fmt(product.deliveryPriceCents)}</span>
              <span className="text-xs bg-green-100 text-green-700 font-semibold px-1 py-0.5 rounded-md">-{Math.round((1 - product.deliveryPriceCents / product.salePriceCents) * 100)}%</span>
            </div>
          ) : (
            <p style={{ color: brand.accentColor }} className="text-lg font-bold tracking-tight">{fmt(product.deliveryPriceCents)}</p>
          )}

          {/* Altura reservada mesmo sem desconto progressivo, pra o CTA não
              subir num card e descer no vizinho. */}
          <div className="min-h-[16px]">
            {melhorFaixa && (
              <p className="text-xs font-semibold text-amber-700 leading-tight">
                🔥 {melhorFaixa.qty}
                {melhorFaixa.qty === 4 ? '+' : ''} por {fmt(melhorFaixa.unitPriceCents)}/un
              </p>
            )}
          </div>

          {!cartItem ? (
            <button
              onClick={() => addItem(product)}
              style={{ backgroundColor: brand.primaryColor }}
              className="w-full min-h-[44px] text-white text-sm font-semibold rounded-xl transition-opacity active:opacity-80"
            >
              Adicionar
            </button>
          ) : (
            <div className="flex flex-col gap-1">
              <div className="flex items-center justify-between bg-gray-50 rounded-xl min-h-[44px] px-1">
                <button
                  onClick={() => cartItem.quantity === 100 ? removeItem(product.id) : updateQuantity(product.id, cartItem.quantity - 100)}
                  style={{ color: brand.primaryColor }}
                  className="min-w-[44px] min-h-[44px] flex items-center justify-center rounded-lg active:opacity-70"
                  aria-label={cartItem.quantity === 100 ? `Remover ${product.name}` : `Diminuir ${product.name}`}
                >
                  {cartItem.quantity === 100 ? <Trash2 size={18} /> : <Minus size={18} />}
                </button>
                <span className="text-base font-bold text-gray-800 tabular-nums">{cartItem.quantity / 100}</span>
                <button
                  onClick={() => updateQuantity(product.id, cartItem.quantity + 100)}
                  style={{ color: brand.primaryColor }}
                  className="min-w-[44px] min-h-[44px] flex items-center justify-center rounded-lg active:opacity-70"
                  aria-label={`Aumentar ${product.name}`}
                >
                  <Plus size={18} />
                </button>
              </div>
              {product.bulkPricing && product.bulkPricing.length > 0 && cartItem.quantity > 100 && (
                <p className="text-xs text-green-700 font-semibold text-center">
                  {fmt(getUnitPriceForQuantity(product, cartItem.quantity / 100))}/un com desconto aplicado
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
